import axios from '../config/api'
import Vue from 'vue'
import moment from 'moment'
import {
  orderDetailTransform,
  createOrderPayloadTransform,
  createVipOrderPayloadTransform,
} from '@/transforms/orderTransform'
import router from '@/router'
import MERCURE_TOPIC from '../config/mercure'
import { useAxiosErrorHandler } from '@/composables/useAxiosErrorHandler'
import { STOCK_CARD_TYPE } from '@/config/stockCard'

const campaignType = process.env.VUE_APP_CAMPAIGN_TYPE ?? 'BNN'

const state = () => ({
  data: [],
  eventSources: {},
  payload: null,
  isError: false,
  isExpired: false,
  isLoading: false,
  submit: {
    data: null,
    isLoading: false,
    isSuccess: false,
    isError: false,
    errorMessage: '',
  },
  cancelVipOrder: {
    data: null,
    isLoading: false,
    isSuccess: false,
    isError: false,
    errorMessage: '',
  },
})

// getters
const getters = {
  total: state => state.data.total_price || 0,
  orderDetail: state => state.data,
  orderStatus: state => state.data.status || '',
  isLoading: state => key => state[key].isLoading || false,
  isSuccess: state => key => state[key].isSuccess || false,
  isError: state => key => state[key].isError || false,
  errorMessage: state => key => state[key].errorMessage || '',
}

// actions
const actions = {
  async getOrder({ commit }, { uuid, slot_table, payment_decision }) {
    commit('setOrder')
    await axios
      .get(`/orders/${uuid}`)
      .then(response => {
        commit('setOrderSuccess', {
          ...response.data.data,
          slot_table: slot_table ?? null,
          payment_decision: payment_decision,
        })
      })
      .catch(error => {
        if (error.response.status === 500) {
          router.push('/404')
        }
        commit('setOrderError')
      })
  },

  /* Subscribe to order status updates with Mercure SSE */
  subscribeOrderStatus({ commit, state }, uuid) {
    // Check if already subscribed and connecting
    if (
      uuid &&
      state.eventSources[uuid] &&
      state.eventSources[uuid].readyState === 0
    ) {
      return
    }

    const hubUrl = process.env.VUE_APP_HUB_MERCURE_URL
    const env =
      process.env.NODE_ENV !== 'production' ? 'dev' : 'production'
    const topic = `${env}.${MERCURE_TOPIC.ORDER_STATUS}.${uuid}`

    const url = `${hubUrl}?topic=${topic}`

    const es = new EventSource(url)

    state.eventSources[uuid] = es

    state.eventSources[uuid].onmessage = event => {
      const data = JSON.parse(event.data)

      if (data.data.status) {
        commit('setOrderStatusSuccess', data.data.status)
      }
    }
  },
  unsubscribeOrderStatus({ state }, uuid) {
    if (uuid && state.eventSources[uuid]) {
      state.eventSources[uuid].close()
      delete state.eventSources[uuid]
    }
  },

  async getOrderLists({ commit }) {
    const headers = {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('tokenOTP')}`,
      },
    }

    commit('getOrderListsRequest')
    await axios
      .get(`/user/order-history/${campaignType}`, headers)
      .then(response => {
        commit('getOrderListsRequestSuccess', response.data.data)
      })
      .catch(() => {
        commit('getOrderListsRequestFailure')
        localStorage.removeItem('tokenOTP')
      })
  },

  getOrderDetail({ commit }, { uuid, slot_table }) {
    commit('setOrder')
    axios
      .get(`/user/order-detail/${uuid}`)
      .then(response => {
        commit('setOrderSuccess', {
          ...response.data.data,
          slot_table: slot_table ?? null,
        })
      })
      .catch(() => {
        commit('setOrderError')
      })
    // }
  },

  async extendTimePayAtStore({ commit }, payload) {
    commit('submitRequest')
    await axios
      .post('/user/extend-pay-at-store', payload)
      .then(() => {
        commit('submitRequestSuccess', null)

        Vue.$toast.success('ขยายเวลาชำระเงินสำเร็จ', {
          duration: 5000,
          position: 'top',
        })
      })
      .catch(error => {
        commit('submitRequestFailure')

        let message = error.response.data.message
          ? error.response.data.message
          : 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง'

        Vue.$toast.error(message, {
          duration: 5000,
          position: 'top',
        })
      })
  },

  isPayAtStoreExpired({ commit }) {
    commit('setOrderExpired')
  },

  async confirmPayment({ commit }, payload) {
    commit('submitRequest')

    const accessToken = Vue.prototype.$cookies.get('token')
    // localStorage.getItem('token')

    if (!accessToken) {
      Vue.$toast.error('กรุณาเข้าสู่ระบบ', {
        duration: 5000,
        position: 'top',
      })
      return
    }

    await axios
      .post('/orders/stamp-pay-at-store', payload, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })
      .then(() => {
        commit('submitRequestSuccess', null)

        Vue.$toast.success('ยืนยันการชำระเงินสำเร็จ', {
          duration: 5000,
          position: 'top',
        })
      })
      .catch(error => {
        const message = useAxiosErrorHandler().handleError(error)
        commit('submitRequestFailure', message)
      })
  },

  async inquiryTransactionRequest({ commit }, uuid) {
    commit('submitRequest')

    const transformedPayload = {
      order_id: uuid,
    }

    await axios
      .post('/orders/inquiry', transformedPayload)
      .then(res => {
        const transformedData = {
          message: res.data.data.message,
        }

        commit('submitRequestSuccess', transformedData)
      })
      .catch(error => {
        commit('submitRequestFailure', error.response.data.message)

        let message = error.response.data.message
          ? error.response.data.message
          : 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง'

        Vue.$toast.error(message, {
          duration: 5000,
          position: 'top',
        })
      })
  },

  async createOrder({ commit, rootState }, { payload, is_vip }) {
    commit('submitRequest')

    const queryParam = router.currentRoute.query
    const coupons = rootState.cart?.coupons

    const transformed = is_vip
      ? createVipOrderPayloadTransform(payload, {
          coupons,
          queryParam,
        })
      : createOrderPayloadTransform(payload, {
          coupons,
          queryParam,
        })

    return await axios
      .post('/orders', transformed)
      .then(res => {
        commit('submitRequestSuccess', res.data)

        commit('recaptcha/setReset', true, { root: true })
        setTimeout(() => {
          commit('recaptcha/setReset', false, { root: true })
        }, 3000)

        return res.data
      })
      .catch(error => {
        const message = error.response?.data?.message
          ? error.response.data.message
          : 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง'

        const { t, code } = queryParam

        if (code) {
          const uid = localStorage.getItem(code)

          if (uid) {
            Vue.$toast.warning('URL หมดอายุหรือถูกใช้ไปแล้ว', {
              duration: 5000,
              position: 'top',
            })

            router.push({
              name:
                t === STOCK_CARD_TYPE.STORE
                  ? 'ConfirmPayment'
                  : 'Thank',
              params: { uid },
            })
          } else {
            Vue.$toast.error(message, {
              duration: 5000,
              position: 'top',
            })
          }
        } else {
          Vue.$toast.error(message, {
            duration: 5000,
            position: 'top',
          })
        }

        commit('submitRequestFailure', message)
      })
  },

  async submitVipPayment({ commit }, { uuid, payload }) {
    commit('submitRequest')

    try {
      const response = await axios.post(
        `/vip-payment/${uuid}/submit`,
        payload,
      )

      commit('submitRequestSuccess', response.data.data)

      // Set order details in the state after successful submission
      commit('setOrderSuccess', response.data.data.order)
    } catch (error) {
      const message = useAxiosErrorHandler().handleError(error)
      commit('submitRequestFailure', message)
    }
  },

  async cancelVipOrder({ commit }, { uuid, code }) {
    commit('cancelVipOrderRequest')

    try {
      const response = await axios.post(
        `/vip-orders/${uuid}/cancel`,
        { code },
      )

      commit('cancelVipOrderSuccess', response.data.data)
    } catch (error) {
      const message = useAxiosErrorHandler().handleError(error)
      commit('cancelVipOrderFailure', message)
    }
  },
}

// mutations
const mutations = {
  setOrder(state) {
    state.isLoading = true
    state.isError = false
  },
  setOrderSuccess(state, payload) {
    state.isLoading = false
    state.data = orderDetailTransform(payload)
    state.isError = false
  },
  setOrderError(state) {
    state.isLoading = false
    state.isError = true
  },
  setOrderStatusSuccess(state, payload) {
    state.data = {
      ...state.data,
      status: payload,
    }
  },
  setOrderPaymentMethod(state, payload) {
    state.data = {
      ...state.data,
      ...payload,
    }
  },
  setReceiveBranchName(state, branchName) {
    if (state.data) {
      state.data.receive_branch_name = branchName
    }
  },

  getOrderListsRequest(state) {
    state.isLoading = true
    state.isError = false
  },
  getOrderListsRequestSuccess(state, payload) {
    state.isLoading = false
    state.data = payload
    state.isError = false
  },
  getOrderListsRequestFailure(state) {
    state.isLoading = false
    state.isError = true
  },
  submitRequest(state) {
    state.submit.isLoading = true
    state.submit.isError = false
    state.submit.isSuccess = false
    state.submit.errorMessage = ''
  },
  submitRequestSuccess(state, event) {
    state.submit.data = event
    state.submit.isLoading = false
    state.submit.isSuccess = true
  },
  submitRequestFailure(state, errorMessage) {
    state.submit.isLoading = false
    state.submit.isError = true
    state.submit.errorMessage =
      errorMessage || 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง'
  },
  submitRequestReset(state) {
    state.submit.data = null
    state.submit.isLoading = false
    state.submit.isSuccess = false
    state.submit.isError = false
    state.submit.errorMessage = ''
  },
  cancelVipOrderRequest(state) {
    state.cancelVipOrder.isLoading = true
    state.cancelVipOrder.isError = false
    state.cancelVipOrder.isSuccess = false
    state.cancelVipOrder.errorMessage = ''
  },
  cancelVipOrderSuccess(state, payload) {
    state.cancelVipOrder.data = payload
    state.cancelVipOrder.isLoading = false
    state.cancelVipOrder.isSuccess = true
  },
  cancelVipOrderFailure(state, errorMessage) {
    state.cancelVipOrder.isLoading = false
    state.cancelVipOrder.isError = true
    state.cancelVipOrder.errorMessage =
      errorMessage || 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง'
  },
  setOrderExpired(state) {
    state.isExpired =
      moment().isAfter(
        moment.unix(state.data.pay_at_store_expire_at_timestamp_bkk),
      ) ?? false
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
